import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaPlaybackRateButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import downloadHandler from '@lib/utils/downloadHandler';
import { formatPlaybackTime } from '@lib/utils/dateTimeHelpers';
import useVideoWatchedSeconds from './useVideoWatchedSeconds';
import { getMarkedRangePosition, getVideoQualityOptions } from './utils';
import styles from './VideoViewer.module.scss';

interface VideoSourceUrls {
  video1080p?: string | null;
  video720p?: string | null;
  video480p?: string | null;
  original?: string | null;
}

export interface VideoTrack {
  src: string;
  srcLang: string;
  label: string;
  kind?: 'subtitles' | 'captions';
}

// A range of the video to mark on the timeline, in seconds (e.g. a verified
// AI citation's moment). `end` null marks only the start.
export interface VideoMarkedRange {
  start: number;
  end: number | null;
}

interface Props {
  fileUrl: string;
  downloadUrl: string;
  posterUrl?: string;
  videoUrls?: VideoSourceUrls;
  /**
   * Future-proof slot for caption/subtitle tracks. BE does not produce
   * caption tracks today.
   */
  tracks?: VideoTrack[];
  /**
   * Fires on every whole-second change in accrued playback time. Used by
   * FileViewerModal to keep a duration that outlives this component, which
   * unmounts on close.
   */
  onWatchedSecondsChange?: (seconds: number) => void;
  onFullScreenToggle?: () => void;
  isFullScreen?: boolean;
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  /** Second the video opens at; read when the video's metadata loads. */
  initialTime?: number;
  /** Range marked on the timeline, with a caption to replay it. */
  markedRange?: VideoMarkedRange;
}

const SEEK_OFFSET_SECONDS = 10;

export default function VideoViewer(props: Props) {
  const {
    fileUrl,
    downloadUrl,
    posterUrl,
    videoUrls,
    tracks,
    onWatchedSecondsChange,
    onFullScreenToggle,
    isFullScreen,
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
    initialTime,
    markedRange,
  } = props;
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [intrinsicRatio, setIntrinsicRatio] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  // ReactPlayer re-applies `volume` and `playbackRate` to the element on every
  // render (defaulting to 1), so mirror what the user picks in the controls
  // back into props — otherwise any re-render would reset them.
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Read by the metadata handler, so restoring the rate after a quality switch
  // doesn't depend on that handler's closure.
  const playbackRateRef = useRef(1);
  playbackRateRef.current = playbackRate;
  // `initialTime` is applied once, on the first metadata load.
  const hasAppliedInitialTimeRef = useRef(false);
  // Chosen rendition label; null = best available.
  const [selectedQuality, setSelectedQuality] = useState<string | null>(null);
  const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(false);
  // Playback state to restore once the newly picked rendition has loaded.
  const resumeRef = useRef<{ time: number; wasPlaying: boolean } | null>(null);

  useVideoWatchedSeconds({ videoRef, onWatchedSecondsChange });

  const qualityOptions = useMemo(
    () => getVideoQualityOptions(videoUrls),
    [videoUrls],
  );
  const qualityOptionsRef = useRef(qualityOptions);
  qualityOptionsRef.current = qualityOptions;
  const activeQuality =
    qualityOptions.find((option) => option.label === selectedQuality) ??
    qualityOptions[0];
  const activeSrc = activeQuality?.src ?? fileUrl;
  // The rendition the element has loaded; a change of `activeSrc` reloads.
  const loadedSrcRef = useRef(activeSrc);

  const handleLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      const { videoWidth, videoHeight } = video;
      if (videoWidth > 0 && videoHeight > 0) {
        setIntrinsicRatio(videoWidth / videoHeight);
      }
      if (Number.isFinite(video.duration)) setDuration(video.duration);
      // The browser may have fallen back to a lower rendition (see the
      // <source> list below): show that one as selected without reloading.
      const playing = qualityOptionsRef.current.find(
        (option) => option.src === video.currentSrc,
      );
      if (playing && playing.src !== loadedSrcRef.current) {
        loadedSrcRef.current = playing.src;
        setSelectedQuality(playing.label);
      }
      const pendingInitialTime =
        !hasAppliedInitialTimeRef.current &&
        typeof initialTime === 'number' &&
        initialTime > 0
          ? initialTime
          : null;
      if (resumeRef.current) {
        const { wasPlaying } = resumeRef.current;
        // A switch picked before the first load captured a meaningless
        // position: start where `initialTime` asks instead.
        const time = pendingInitialTime ?? resumeRef.current.time;
        resumeRef.current = null;
        hasAppliedInitialTimeRef.current = true;
        video.currentTime = Math.min(time, video.duration || time);
        // load() resets the rate to its default (ignored by handleRateChange
        // while the switch is pending).
        video.playbackRate = playbackRateRef.current;
        if (wasPlaying) video.play().catch(() => undefined);
        return;
      }
      if (pendingInitialTime !== null) {
        hasAppliedInitialTimeRef.current = true;
        video.currentTime = Number.isFinite(video.duration)
          ? Math.min(pendingInitialTime, video.duration)
          : pendingInitialTime;
      }
    },
    [initialTime],
  );

  const handleRateChange = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      // A quality switch's load() resets the rate to 1; that isn't the user's
      // choice, and the metadata handler restores theirs.
      if (resumeRef.current) return;
      setPlaybackRate(event.currentTarget.playbackRate);
    },
    [],
  );

  // ReactPlayer doesn't forward `poster` to its <video>, so set it directly.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (posterUrl) {
      video.poster = posterUrl;
    } else {
      video.removeAttribute('poster');
    }
  }, [posterUrl]);

  const handleDurationChange = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const { duration: nextDuration } = event.currentTarget;
      if (Number.isFinite(nextDuration)) setDuration(nextDuration);
    },
    [],
  );

  // A different file is a fresh open: apply its initial time again.
  useEffect(() => {
    hasAppliedInitialTimeRef.current = false;
  }, [fileUrl]);

  const playMarkedRange = useCallback(() => {
    const video = videoRef.current;
    if (!video || !markedRange) return;
    video.currentTime = markedRange.start;
    video.play().catch(() => {
      // Autoplay policies may reject play(); the user can press play.
    });
  }, [markedRange]);

  // The picked rendition first, then the others highest first: if it fails to
  // load, the browser falls back down the list on its own.
  const sources = activeQuality
    ? [
        activeQuality,
        ...qualityOptions.filter((option) => option !== activeQuality),
      ]
    : [];

  const handleQualitySelect = useCallback(
    (label: string) => {
      setIsQualityMenuOpen(false);
      if (label === activeQuality?.label) return;
      const video = videoRef.current;
      if (video) {
        resumeRef.current = {
          time: video.currentTime,
          wasPlaying: !video.paused,
        };
      }
      setSelectedQuality(label);
    },
    [activeQuality],
  );

  const qualityRef = useRef<HTMLDivElement>(null);
  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!isQualityMenuOpen) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      if (!qualityRef.current?.contains(event.target as Node)) {
        setIsQualityMenuOpen(false);
      }
    };
    // Captured and stopped, so this Escape closes only the menu — not the
    // modal the viewer sits in, which also closes on Escape.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      setIsQualityMenuOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isQualityMenuOpen]);

  // <source> swaps are not picked up by an already-loaded element.
  useEffect(() => {
    if (loadedSrcRef.current === activeSrc) return;
    loadedSrcRef.current = activeSrc;
    videoRef.current?.load();
  }, [activeSrc]);

  const markedPosition = getMarkedRangePosition(markedRange, duration);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.actionsRow}>
          {onFullScreenToggle && (
            <IconButton
              icon={isFullScreen ? 'fullscreen_exit' : 'fullscreen'}
              onClick={onFullScreenToggle}
              symbolsOutlined
            />
          )}
          {markedRange && (
            <Button
              buttonType={ButtonTypes.secondaryOutlined}
              buttonSize={ButtonSizes.large}
              leftIcon="replay"
              buttonText={t('video-play-marked-range', {
                time: formatPlaybackTime(markedRange.start),
              })}
              onClick={playMarkedRange}
            />
          )}
        </div>
        <div className={styles.actionsRow}>
          {useDownloadTool && (
            <IconButton
              icon="download"
              onClick={() => downloadHandler(downloadUrl)}
              symbolsOutlined
            />
          )}
          {useFavoriteTool && onFavoriteToggle && (
            <IconButton
              icon={isFavorited ? 'bookmark_star' : 'bookmark'}
              onClick={onFavoriteToggle}
              symbolsOutlined
              filled={isFavorited}
              isLoading={isFavoriteLoading}
              type={IconButtonTypes.PrimaryOutlined}
            />
          )}
        </div>
      </div>
      <div className={styles.videoWrap}>
        <MediaController
          className={styles.controller}
          style={intrinsicRatio ? { aspectRatio: intrinsicRatio } : undefined}
        >
          <ReactPlayer
            ref={videoRef}
            slot="media"
            className={styles.video}
            controls={false}
            preload="metadata"
            playsInline
            volume={volume}
            playbackRate={playbackRate}
            width="100%"
            height="100%"
            onLoadedMetadata={handleLoadedMetadata}
            onDurationChange={handleDurationChange}
            onVolumeChange={(event) => setVolume(event.currentTarget.volume)}
            onRateChange={handleRateChange}
          >
            {sources.length ? (
              sources.map((source) => (
                <source key={source.src} src={source.src} type="video/mp4" />
              ))
            ) : (
              <source src={fileUrl} />
            )}
            {(tracks || []).map((track) => (
              <track
                key={`${track.kind ?? 'subtitles'}-${track.srcLang}-${track.src}`}
                src={track.src}
                srcLang={track.srcLang}
                label={track.label}
                kind={track.kind ?? 'subtitles'}
              />
            ))}
          </ReactPlayer>
          <MediaControlBar className={styles.controlBar}>
            <MediaPlayButton />
            <MediaSeekBackwardButton seekOffset={SEEK_OFFSET_SECONDS} />
            <MediaSeekForwardButton seekOffset={SEEK_OFFSET_SECONDS} />
            <div className={styles.timeRangeWrap}>
              <MediaTimeRange className={styles.timeRange} />
              {markedPosition && (
                <span
                  className={styles.markedRange}
                  data-start-only={markedPosition.length === null || undefined}
                  style={
                    {
                      '--start': markedPosition.start,
                      '--length': markedPosition.length ?? 0,
                    } as React.CSSProperties
                  }
                />
              )}
            </div>
            <MediaTimeDisplay showDuration />
            <MediaMuteButton />
            <MediaVolumeRange />
            <MediaPlaybackRateButton />
            {qualityOptions.length > 1 && activeQuality && (
              <div className={styles.quality} ref={qualityRef}>
                <button
                  type="button"
                  className={styles.qualityButton}
                  aria-label={t('video-quality')}
                  aria-haspopup="menu"
                  aria-expanded={isQualityMenuOpen}
                  onClick={() => setIsQualityMenuOpen((open) => !open)}
                >
                  {activeQuality.label}
                  <MaterialIcon
                    icon="expand_less"
                    symbolsOutlined
                    size="md-16"
                  />
                </button>
                {isQualityMenuOpen && (
                  <ul className={styles.qualityMenu} role="menu">
                    {qualityOptions.map((option) => (
                      <li key={option.label} role="none">
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={option.label === activeQuality.label}
                          className={styles.qualityItem}
                          data-active={
                            option.label === activeQuality.label || undefined
                          }
                          onClick={() => handleQualitySelect(option.label)}
                        >
                          {option.label}
                          <span className={styles.qualityCheck}>
                            {option.label === activeQuality.label && (
                              <MaterialIcon icon="check" size="md-16" />
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <MediaFullscreenButton />
          </MediaControlBar>
        </MediaController>
      </div>
    </div>
  );
}
